/**
 * Tier-2 — which models an org's roles, teams and members may use.
 *
 * The ORG layer of model access
 * (docs/plans/2026-07-30_MODEL_ACCESS_GOVERNANCE.md). It can only NARROW what
 * the platform already allows: a restriction on a model the operator hid does
 * nothing, because the resolver intersects with the visible set. So this screen
 * never promises access — only withholds it.
 *
 * ── Three tables, and why the axis is this way round ─────────────────
 *
 * Roles, teams and members each get the SAME control: a table listing every
 * subject, with a tick box per model. See everything, set anything, no picker
 * in the way.
 *
 * The load-bearing decision is which way the grid runs. **Subjects are ROWS and
 * models are COLUMNS**, because only one of those axes is bounded:
 *
 *  - Models are a curated catalogue that grows slowly (8 today). Eight columns
 *    fit, and the count is the same on every screen in this app.
 *  - Teams and members are unbounded and org-specific. Ten teams, fifty
 *    members.
 *
 * Rows are what a browser scrolls, filters and sticky-headers well; columns are
 * not. Teams used to be a column each, which is why ten of them overflowed
 * sideways with no way to find one — and members had no table at all, just a
 * dropdown that showed one person at a time. Transposing fixes both with one
 * shape rather than two special cases, and a long list stays manageable via the
 * filter box and a scroll container under a sticky header.
 *
 * The subject column is sticky, so the name stays put when the model columns
 * scroll horizontally on a narrow pane.
 *
 * ── Subjects that no longer exist ────────────────────────────────────
 *
 * `model_restrictions.subject_id` is bare text with no FK, so nothing reaps a
 * row when a team is deleted or a member removed — and on the user axis the id
 * IS the email, which makes a leftover row LIVE: re-invite the same person and
 * their old blocks apply again (CLAUDE.md, "governance table keyed by bare
 * text"). A table built only from the CURRENT team/member list cannot show one.
 * These tables append any stored-but-unknown subject at the bottom, flagged, so
 * it can be found and untangled — clearing its ticks deletes the rows like any
 * other edit, so it needs no control of its own.
 *
 * ── What a marked cell means, and why it is a CROSS ──────────────────
 *
 * A marked cell is a BLOCK, not an allow. That is the storage model too:
 * absence means allowed, so an org that has configured nothing stores nothing.
 * Keeping the UI on the same polarity keeps the screen and the table saying the
 * same thing — an "allowed" grid would invert on save and read backwards in the
 * audit log.
 *
 * Which is exactly why these are not ticks. A ✓ means "yes, allowed" in every
 * other grid a person has used, so a ticked box for "this model is REFUSED" is
 * the one place the screen could be read as its own opposite. The marked state
 * is therefore a red ✗ and the clear state an empty box — the same polarity as
 * the red "Blocked" chips on the cap editor above. The control is still a
 * checkbox to assistive tech; only its face changed.
 *
 * Precedence, stated on screen because it is not guessable: a member-level
 * block wins; otherwise ANY blocking team blocks; otherwise the role. Mirrors
 * `resolveAllowSharing`.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useDraftState } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { CAP_ROLES, MODEL_CATALOG, PROVIDER_TABS } from '@papan/types'
// Narrow subpath, NOT the `@papan/agent/ui` barrel. That barrel re-exports
// fifteen components — ChatPanel, HeroChat, MarkdownContent (`marked`),
// DiffView (`diff`), plus `lucide-react` — and any one of them with a top-level
// side effect defeats tree-shaking and lands the whole agent chat surface in
// the Settings bundle, for one 6-line function. `ui/storage/shared.tsx` records
// the same reasoning for why orgs avoids `lucide-react` at all.
import { invalidateModelCatalog } from '@papan/agent/ui/model-catalog'
import {
  budgetApi,
  type ModelRestrictionRow,
  type MemberUsageRow,
  type TeamUsageRow,
} from '../../client/budget'
import { request, inBatch, pinnedBatch, type PinnedBatch } from '@papan/api-client'
import { Section } from './bits'

/** Mock models never reach a provider and are gated by an email allow-list —
 *  not something an org restricts. */
const CATALOG_MODELS = MODEL_CATALOG.filter(
  (m) => !PROVIDER_TABS.find((t) => t.id === m.provider)?.mockOnly,
)

/** The same role set the cap editor uses, so a role that can hold a budget can
 *  hold a model restriction. (`noaccess` is not in CAP_ROLES — it can never run
 *  the agent, so there is nothing to restrict.) */
const ROLES = CAP_ROLES

/** Past this many rows a table gets a filter box. Below it, a filter is one
 *  more control to read on a screen that already has several. */
const FILTER_THRESHOLD = 8

/** Past this many rows the table body scrolls under its own sticky header
 *  instead of pushing the rest of the page down. */
const SCROLL_THRESHOLD = 12

type SubjectType = 'role' | 'team' | 'user'
const key = (type: SubjectType, id: string) => `${type}:${id}`

/** A translate function narrowed to what the subcomponent uses, so it can be
 *  given `t` without importing the i18n types. */
type T = (key: string, opts?: Record<string, unknown>) => string

/** One table row. `missing` = stored against a team/member this org no longer
 *  has. */
interface Subject {
  id: string
  name: string
  /** Secondary line — a member's role, so the admin can see which row of the
   *  role table this one is overriding. */
  sub?: string
  missing?: boolean
}

/**
 * What the page can do to this grid from its own Save bar.
 *
 * An imperative handle rather than lifting `drafts` into `BudgetsEditor`: the
 * staged state here is a map keyed by subject with its own orphan handling,
 * catalog reconciliation and per-subject failure collection, none of which the
 * cap tables share. Hoisting it would move ~80 lines of unrelated logic into a
 * component that has no use for it, to gain one button. The parent needs
 * exactly two verbs and a count.
 */
export interface ModelRestrictionsHandle {
  /** Commit staged subjects. Resolves with what landed and what did not —
   *  never throws, and never toasts; the page composes one message. */
  /** #1316 review round 2: `batch` — the page Save's pinned batch (every subject written as who pressed Save). */
  save: (batch?: PinnedBatch) => Promise<{ saved: number; failed: string[] }>
  /** Drop staged edits. */
  discard: () => void
}

export const ModelRestrictionsEditor = forwardRef<ModelRestrictionsHandle, {
  tenantId: string
  canWrite: boolean
  members: MemberUsageRow[]
  teams: TeamUsageRow[]
  /** Reports the staged-subject count upward, so the page's single bar can
   *  say how many model changes are pending alongside the cap count. */
  onDirtyChange?: (count: number) => void
}>(function ModelRestrictionsEditor({
  tenantId,
  canWrite,
  members,
  teams,
  onDirtyChange,
}, ref) {
  const { t } = useTranslation('core-ui')

  /** How many catalog models this tenant can actually reach, from the same
   *  endpoint the chat picker uses — so the count cannot drift from what a
   *  member sees in the model list. `null` while unknown or unreadable. */
  const [allowed, setAllowed] = useState<number | null>(null)
  const [rows, setRows] = useState<ModelRestrictionRow[] | null>(null)
  // #897 — staged blocks, kept across a phone/desktop layout swap.
  const [drafts, setDrafts, { clear: clearDrafts }] = useDraftState<Record<string, string[]>>(`budget:${tenantId}:models`, {})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * `keepStaged` preserves the drafts of subjects that did NOT save.
   *
   * A plain reload clears every draft, so a partial save discarded the edits
   * for exactly the subjects it had just reported as failed — the loop below
   * collects failures precisely so those edits survive to be retried, and then
   * the reload threw them away.
   *
   * It matters more since the page went to ONE Save button: the caps half
   * keeps failed rows staged, so without this a single click produced two
   * different failure behaviours on one screen.
   */
  const load = useCallback(async (keepStaged?: string[]) => {
    try {
      const res = await budgetApi.getModelRestrictions(tenantId)
      setRows(res.restrictions)
      // Only a reload AFTER a save trims the drafts (to the subjects that did
      // not save). The mount-time load leaves them: a draft a phone/desktop
      // layout swap carried over (#897) must not be wiped by the new screen.
      if (keepStaged !== undefined) {
        if (keepStaged.length === 0) clearDrafts()
        else setDrafts(prev => {
          const next: Record<string, string[]> = {}
          // Key PRESENCE, not truthiness: an empty array is a real draft
          // ("clear every block for this subject") and a falsy filter drops it.
          for (const k of keepStaged) if (k in prev) next[k] = prev[k]
          return next
        })
      }
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load model restrictions')
      setRows([])
    }
  }, [tenantId, setDrafts, clearDrafts])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    let alive = true
    request<{ models?: { id: string }[] }>('/api/agent/models')
      .then(r => { if (alive) setAllowed(r.models?.length ?? null) })
      .catch(() => { if (alive) setAllowed(null) })
    return () => { alive = false }
  }, [])

  /** Stored blocked-set per subject, from the server rows. */
  const stored = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const r of rows ?? []) {
      const k = key(r.subjectType, r.subjectId)
      map[k] = [...(map[k] ?? []), r.modelId]
    }
    return map
  }, [rows])

  /**
   * One `Set` per subject, built once per render instead of a fresh array and
   * a linear `.includes` per CELL.
   *
   * The transpose made large member lists the point of this screen, and the
   * cell count is subjects × models — 400 lookups for a 50-member org, redone
   * on every keystroke in the filter box and on every toggle (which replaces
   * the whole `drafts` object).
   */
  const blockedSets = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const [k, v] of Object.entries(stored)) map.set(k, new Set(v))
    for (const [k, v] of Object.entries(drafts)) map.set(k, new Set(v))
    return map
  }, [stored, drafts])

  const EMPTY: ReadonlySet<string> = new Set()
  const blockedFor = (type: SubjectType, id: string): ReadonlySet<string> =>
    blockedSets.get(key(type, id)) ?? EMPTY

  const toggle = (type: SubjectType, id: string, modelId: string) => {
    const k = key(type, id)
    const now = drafts[k] ?? stored[k] ?? []
    const next = now.includes(modelId) ? now.filter((m) => m !== modelId) : [...now, modelId]
    // Back to the stored blocks (any order): no edit for this subject — kept,
    // it hid later reloads of it and Save wrote it back (#897).
    const base = stored[k] ?? []
    const unchanged = next.length === base.length && next.every((m) => base.includes(m))
    setDrafts((cur) => {
      if (!unchanged) return { ...cur, [k]: next }
      if (!(k in cur)) return cur
      const { [k]: _dropped, ...rest } = cur
      return rest
    })
  }

  // The page's Save/Discard reach the grid through this. Depends on `save`,
  // which closes over `drafts` and `dirty`, so it must be re-created when they
  // change — a handle captured once would commit a stale draft.
  useImperativeHandle(ref, () => ({
    save,
    discard: () => clearDrafts(),
  }))

  const roleLabel = useCallback(
    (id: string) => t(`settings.budget.role.${id}`, { defaultValue: id }),
    [t],
  )

  /**
   * Catalog models PLUS any model a stored restriction names that the catalog
   * no longer offers — a retired model id, or a mock one the filter drops.
   *
   * The subject axis got an orphan pass; the MODEL axis had the same hole.
   * `resolveModelAccess` keeps enforcing a row whatever the catalog says, so a
   * restriction on a model with no column was invisible AND unclearable:
   * `toggle` seeds from `stored[k]`, so clearing every visible cell preserved
   * the hidden entry straight through save. An admin could not lift a block
   * that was still blocking.
   */
  const models = useMemo(() => {
    const known = new Set(CATALOG_MODELS.map((m) => m.id))
    const extra = new Set<string>()
    for (const r of rows ?? []) if (!known.has(r.modelId)) extra.add(r.modelId)
    return [
      ...CATALOG_MODELS.map((m) => ({ id: m.id, label: m.label, missing: false })),
      ...[...extra].sort().map((id) => ({ id, label: id, missing: true })),
    ]
  }, [rows])

  /**
   * Stored subject ids of one type that the org no longer has, appended to that
   * type's table so a restriction can never outlive its subject unseen.
   */
  const orphansOf = useCallback((type: SubjectType, known: Set<string>): Subject[] => {
    const prefix = `${type}:`
    return Object.keys(stored)
      .filter((k) => k.startsWith(prefix))
      // Split once — an email can't contain ':', but rejoin defensively.
      .map((k) => k.slice(prefix.length))
      .filter((id) => !known.has(id))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((id) => ({ id, name: id, missing: true }))
  }, [stored])

  // The role axis needs the orphan pass too. `subject_id` is bare text with no
  // FK on EVERY axis, so a role renamed or dropped from `CAP_ROLES` leaves rows
  // that `resolveModelAccess` still enforces while the editor cannot show them
  // — an admin unable to clear a block that is still blocking. Building this
  // list purely from `ROLES` upheld the docblock's "a restriction can never
  // outlive its subject unseen" on two axes out of three.
  const roleSubjects = useMemo<Subject[]>(() => [
    ...ROLES.map((r) => ({ id: r.id, name: roleLabel(r.id) })),
    ...orphansOf('role', new Set(ROLES.map((r) => r.id))),
  ], [roleLabel, orphansOf])

  const teamSubjects = useMemo<Subject[]>(() => [
    ...teams.map((tm) => ({ id: tm.id, name: tm.name })),
    ...orphansOf('team', new Set(teams.map((tm) => tm.id))),
  ], [teams, orphansOf])

  const memberSubjects = useMemo<Subject[]>(() => [
    ...members.map((m) => ({ id: m.email, name: m.email, sub: roleLabel(m.role) })),
    ...orphansOf('user', new Set(members.map((m) => m.email))),
  ], [members, roleLabel, orphansOf])

  const dirty = Object.keys(drafts).filter((k) => {
    const before = [...(stored[k] ?? [])].sort().join('|')
    const after = [...drafts[k]].sort().join('|')
    return before !== after
  })

  /**
   * Commit every staged subject and REPORT, rather than announce.
   *
   * It used to toast its own success/failure and own its own Save button. Both
   * moved to the parent when the page went to a single sticky bar (decision 5):
   * two bars at `sticky bottom-3` could stack, and "Save" that meant "save half
   * of this page" is not what the word says. So this returns a result and the
   * caller composes ONE message covering caps and models together.
   */
  // Report upward whenever the staged count changes, so the page's single bar
  // can name model changes beside cap changes. `dirty` is recomputed every
  // render, so the dep is its LENGTH — the array identity always differs and
  // would fire this on every keystroke in the filter box.
  useEffect(() => { onDirtyChange?.(dirty.length) }, [dirty.length, onDirtyChange])

  const save = async (batch: PinnedBatch = pinnedBatch()): Promise<{ saved: number; failed: string[] }> => {
    setSaving(true)
    let done = 0
    // Per-subject, NOT one try around the loop. The transpose raised the dirty
    // count from about one to dozens, and a single throw used to abandon every
    // subject after it AND then `load()` — which resets `drafts` — so one
    // rejected subject silently destroyed edits the admin had made to every
    // other row and never sent. Failures are collected and named instead; the
    // subjects that can be saved, are.
    const failed: string[] = []
    /** Draft keys whose write was refused — kept staged by `load` below. */
    const failedKeys: string[] = []
    try {
      for (const k of dirty) {
        const [type, ...rest] = k.split(':')
        // Emails contain no ':' but ids are rejoined defensively — a split that
        // silently truncated a subject id would write a restriction against the
        // wrong subject.
        const subjectId = rest.join(':')
        try {
          await inBatch(batch, () => budgetApi.setModelRestrictions(
            tenantId, type as SubjectType, subjectId, drafts[k],
          ))
          done++
        } catch (e: any) {
          // Named, because "Team not found" alone leaves the admin hunting
          // which of a dozen rows it referred to.
          failed.push(`${subjectId}: ${e?.message || 'failed'}`)
          failedKeys.push(k)
        }
      }
      // Keep the subjects that did not land, so their staged ticks survive for
      // a retry instead of being silently reverted to server truth.
      if (!batch.ended()) await load(failedKeys)
      return { saved: done, failed }
    } finally {
      // Drop the picker's module-scoped catalog cache whatever the outcome.
      // A partial save (subject 3 of 5 rejected) still changed policy for the
      // subjects that landed, so the failure path needs this as much as the
      // success path.
      //
      // This is a courtesy, not the enforcement: the server refuses a
      // restricted model at enqueue regardless. Without it an admin's save is
      // invisible to any chat tab that is already open — the cache is
      // module-scoped, so even remounting the picker reuses it, and the
      // "your model was restricted, switching away" swap in ModelSelector
      // never fires. The admin sees the restriction take hold and the user
      // sees nothing until they reload.
      //
      // Only refreshes THIS browser. Another member's open tab keeps the old
      // picker until their next load; they get the server's 403 in the
      // meantime, which is correct but reads as a stale UI. A realtime
      // invalidation event is the real fix if that becomes a complaint.
      invalidateModelCatalog()
      setSaving(false)
    }
  }

  if (!rows) {
    return (
      <Section title={t('settings.budget.models.restrictTitle')}>
        <p className="themed-text-muted text-xs">{error ?? t('common:loading')}</p>
      </Section>
    )
  }

  const busy = saving || !canWrite

  return (
    <Section
      title={t('settings.budget.models.restrictTitle')}
      desc={t('settings.budget.models.restrictDesc')}
    >
      <p className="themed-text-muted text-[11px] mb-2">{t('settings.budget.models.precedence')}</p>
      {/* M6 — the screen told an admin they may only narrow what the platform
          allows, and never showed what that allowance IS. So "why can't we use
          model X?" was unanswerable from inside the org: a model the platform
          or the plan withholds is simply absent, by design, and the absence is
          indistinguishable from a model that never existed.
          Stating the two counts does not disclose WHICH models are withheld
          (that is deliberately the platform's business) — it tells the admin
          the list they are looking at is already a subset, which is the fact
          they were missing. */}
      {allowed !== null && (
        <p className="themed-text-muted text-[11px] mb-2">
          {t('settings.budget.models.platformAllowance', { shown: allowed, total: MODEL_CATALOG.length })}
        </p>
      )}
      <BlockLegend t={t} />

      {/* Weakest precedence first, so reading the screen downwards is reading
          the resolver: role, then team, then member. */}
      <SubjectTable
        title={t('settings.budget.models.roleCol')}
        subjectHeader={t('settings.budget.col.role')}
        subjects={roleSubjects}
        type="role"
        blockedFor={blockedFor}
        models={models}
        onToggle={toggle}
        busy={busy}
        t={t}
      />

      <SubjectTable
        title={t('settings.budget.models.teamCol')}
        subjectHeader={t('settings.budget.col.team')}
        subjects={teamSubjects}
        type="team"
        empty={t('settings.budget.noTeams')}
        blockedFor={blockedFor}
        models={models}
        onToggle={toggle}
        busy={busy}
        t={t}
      />

      {/* `empty` is not optional here in practice: a console-provisioned tenant
          normally has ZERO members and one pending owner invite, so `users`
          comes back empty. Without its own copy the table fell through to the
          filter's no-match string with an empty interpolation — `Nothing
          matches ""` for a filter the admin never used and could not even see
          (it only renders above FILTER_THRESHOLD). */}
      <SubjectTable
        title={t('settings.budget.models.memberCol')}
        subjectHeader={t('settings.budget.col.member')}
        subjects={memberSubjects}
        type="user"
        empty={t('settings.budget.models.noMembers')}
        blockedFor={blockedFor}
        models={models}
        onToggle={toggle}
        busy={busy}
        t={t}
      />

      {/* No Save button here any more — the page owns ONE sticky bar covering
          both the cap tables and this grid (decision 5). Two bars at the same
          screen position could stack, and a "Save" that committed only half of
          a page of staged edits is not what the word says. Dirty state is
          reported upward instead; `save`/`discard` are exposed on the ref. */}
    </Section>
  )
})

/** The ✗ itself, at whatever size the caller needs. */
function CrossMark({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  )
}

/** Empty box = allowed, crossed box = blocked. Rendered once, above all three
 *  tables, because the polarity is the one thing on this screen a reader can
 *  get backwards. */
function BlockLegend({ t }: { t: T }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 themed-text-muted text-[11px]">
      <span className="flex items-center gap-1.5">
        <span className="h-[15px] w-[15px] rounded border themed-border" aria-hidden="true" />
        {t('settings.budget.models.legendAllowed')}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="flex h-[15px] w-[15px] items-center justify-center rounded border border-red-500/70 bg-red-500/80 text-white"
          aria-hidden="true"
        >
          <CrossMark size={10} />
        </span>
        {t('settings.budget.models.legendBlocked')}
      </span>
    </div>
  )
}

/**
 * One cell — may this subject use this model?
 *
 * Deliberately NOT a native checkbox face. A ✓ reads as "yes, allowed"
 * everywhere else, and here the marked state means the model is refused; a
 * ticked box would be the one control on the screen that can be read as its own
 * opposite. So marked renders as a red ✗ and clear as an empty box.
 *
 * It is still a checkbox to assistive tech — `role="checkbox"` + `aria-checked`
 * on a button, the same shape as the `role="switch"` toggles elsewhere in this
 * app — so Space/Enter operate it and the state is announced. The accessible
 * name is "Block <model> for <subject>", so "checked" is heard as blocked
 * rather than as an ambiguous tick.
 *
 * The whole cell is the hit area, not the 15px box inside it: a grid this dense
 * is otherwise a pixel-hunt, on touch especially.
 */
function BlockCell({
  blocked,
  disabled,
  onToggle,
  label,
}: {
  blocked: boolean
  disabled: boolean
  onToggle: () => void
  label: string
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={blocked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onToggle}
      className={`group flex w-full items-center justify-center px-2 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/60 ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      } ${blocked ? 'bg-red-500/10' : ''}`}
    >
      <span
        className={`flex h-[18px] w-[18px] items-center justify-center rounded border transition-colors ${
          blocked
            ? 'border-red-500/70 bg-red-500/80 text-white'
            : `themed-border ${disabled ? '' : 'group-hover:border-red-400/70 group-hover:bg-red-500/10'}`
        } ${disabled ? 'opacity-50' : ''}`}
      >
        {blocked && <CrossMark size={12} />}
      </span>
    </button>
  )
}

/**
 * Subjects × models. A marked cell BLOCKS.
 *
 * Identical for roles, teams and members — the only differences are the table's
 * title and its first column header. That sameness is the point: three subject
 * types that behave the same in the resolver should not need three different
 * controls to configure.
 */
function SubjectTable({
  title,
  subjectHeader,
  subjects,
  type,
  empty,
  blockedFor,
  models,
  onToggle,
  busy,
  t,
}: {
  title: string
  subjectHeader: string
  subjects: Subject[]
  type: SubjectType
  /** Copy for a type that can legitimately have no subjects (teams). */
  empty?: string
  blockedFor: (type: SubjectType, id: string) => ReadonlySet<string>
  /** Catalog models PLUS any stored-but-unknown ones — see `models` in the
   *  parent. Passed in rather than read from the module so a restriction on a
   *  retired model still gets a column it can be cleared from. */
  models: { id: string; label: string; missing: boolean }[]
  onToggle: (type: SubjectType, id: string, modelId: string) => void
  busy: boolean
  t: T
}) {
  const [query, setQuery] = useState('')

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return subjects
    return subjects.filter((s) =>
      s.name.toLowerCase().includes(q) || (s.sub ?? '').toLowerCase().includes(q))
  }, [subjects, query])

  const filterable = subjects.length > FILTER_THRESHOLD
  const scrolls = shown.length > SCROLL_THRESHOLD

  return (
    <div className="mt-3 first:mt-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1.5">
        <h4 className="themed-text text-xs font-medium">{title}</h4>
        {filterable && (
          <>
            <span className="themed-text-muted text-[10px]">
              {t('settings.budget.models.filterCount', { shown: shown.length, total: subjects.length })}
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('settings.budget.models.filterPlaceholder')}
              aria-label={`${title} — ${t('settings.budget.models.filterLabel')}`}
              className="ml-auto themed-input border rounded px-2 py-0.5 text-[11px] w-48 outline-none focus:border-blue-500"
            />
          </>
        )}
      </div>

      {subjects.length === 0 ? (
        <p className="themed-text-muted text-[11px] themed-bg-secondary rounded-lg px-3 py-2.5">
          {empty ?? t('settings.budget.models.noMatches', { query: '' })}
        </p>
      ) : shown.length === 0 ? (
        <p className="themed-text-muted text-[11px] themed-bg-secondary rounded-lg px-3 py-2.5">
          {t('settings.budget.models.noMatches', { query: query.trim() })}
        </p>
      ) : (
        <div
          className={`themed-bg-secondary rounded-lg overflow-x-auto ${
            scrolls ? 'max-h-[26rem] overflow-y-auto' : ''
          }`}
        >
          <table className="w-full text-sm border-collapse">
            <caption className="sr-only">{t('settings.budget.models.tableCaption')}</caption>
            <thead>
              {/* Sticky so the model a column belongs to stays readable while a
                  long member list scrolls under it. */}
              <tr className="border-b themed-border">
                <th className="text-left px-2 py-2 themed-text-secondary text-xs font-medium sticky left-0 top-0 z-30 themed-bg-secondary">
                  {subjectHeader}
                </th>
                {models.map((m) => (
                  <th
                    key={m.id}
                    className="px-2 py-2 themed-text-secondary text-[11px] font-medium whitespace-nowrap text-center sticky top-0 z-20 themed-bg-secondary"
                  >
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            {/* No row-level hover: the cell is the target, and lighting up the
                whole row implies the row itself does something. The cell's own
                hover is the affordance. */}
            <tbody>
              {shown.map((s) => (
                <tr key={s.id} className="border-b themed-border last:border-b-0">
                  <th
                    scope="row"
                    className="text-left px-2 py-1 themed-text text-xs font-normal whitespace-nowrap sticky left-0 z-10 themed-bg-secondary"
                  >
                    <span className="flex items-baseline gap-1.5">
                      <span>{s.name}</span>
                      {s.sub && !s.missing && (
                        <span className="themed-text-muted text-[10px]">{s.sub}</span>
                      )}
                      {s.missing && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300"
                          title={t('settings.budget.models.orphanHint')}
                        >
                          {t('settings.budget.models.orphan')}
                        </span>
                      )}
                    </span>
                  </th>
                  {models.map((m) => (
                    <td key={m.id} className="p-0">
                      <BlockCell
                        blocked={blockedFor(type, s.id).has(m.id)}
                        disabled={busy}
                        onToggle={() => onToggle(type, s.id, m.id)}
                        label={t('settings.budget.models.blockCell', { model: m.label, subject: s.name })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
